import { useState } from "react";
import type { ChapterImage } from "../api";
import { useT } from "../i18n";
import PictureViewer, { usePicturePlace } from "./PictureViewer";

/**
 * ChapterFigure is one picture in a chapter (ADR-0038), as a reader sees it.
 *
 * Shared by the reader's text, the player's transcript and the studio, so a
 * picture looks the same everywhere and the author is never deciding from a
 * preview that differs from the page.
 *
 * `width`/`height` are set on the <img> so the browser reserves the right box
 * before the bytes arrive. Without them a chapter reflows as each picture
 * lands, which on a phone means the line somebody is reading jumps away — and
 * for the player it would fight auto-scroll the whole way down the chapter.
 *
 * The caption deliberately carries **no `dir`**. It sits inside the element
 * that holds the chapter's authored text and already carries `dir="auto"`, and
 * a nested `dir` there is worse than none: the outer element skips any
 * descendant with its own direction when it looks for a first strong
 * character, so a caption at the top of a chapter would leave the chapter
 * itself with nothing to read (CLAUDE.md).
 *
 * In a reader's chapter (PicturePlace is set) the picture is a button that
 * opens it full screen (requirement 0122); in the studio it is just a picture.
 *
 * More space above than below, because the blank line between paragraphs
 * supplies the rest underneath: an even margin left a gap after the caption
 * wide enough to read as a mistake.
 */
export default function ChapterFigure({
  image,
  children,
}: {
  image: ChapterImage;
  /** Studio controls, rendered under the picture. */
  children?: React.ReactNode;
}) {
  const t = useT();
  const place = usePicturePlace();
  const [open, setOpen] = useState(false);
  const picture = (
    <img
      src={image.url}
      alt={image.alt ?? ""}
      width={image.width}
      height={image.height}
      loading="lazy"
      decoding="async"
      data-testid="chapter-image"
      className="h-auto w-full rounded-card bg-sunk"
    />
  );
  return (
    <figure data-testid="chapter-figure" data-image-id={image.id} className="mt-6 mb-3">
      {place ? (
        <button
          type="button"
          aria-label={t.picture.open}
          data-testid="chapter-image-open"
          onClick={() => setOpen(true)}
          className="block w-full cursor-zoom-in"
        >
          {picture}
        </button>
      ) : (
        picture
      )}
      {open && <PictureViewer image={image} onClose={() => setOpen(false)} />}
      {image.caption && (
        <figcaption
          data-testid="chapter-figure-caption"
          className="mt-2 text-center text-sm leading-6 text-muted"
        >
          {image.caption}
        </figcaption>
      )}
      {children}
    </figure>
  );
}
